<script>
  import { onMount, getContext } from "svelte"
  import { UsersRepository, RolesRepository, GroupsRepository } from "../../repositories/index"
  import { _, callout } from "../../stores/index"
  import { AssignmentGrid, MissingPoliciesCallout } from "../../components/index"
  import { Loader, Button, FlexItem } from "@gl/design-system"
  const { isAuthorized } = getContext("isAuthorized")
  import { navigate } from "../../utils/index"

  export let id
  export let role

  let assignmentGrid

  let roleGroups = []
  let roleUsers = []
  let allUsers = []
  let allGroups = []

  let assignmentGridObj = {
    current: [],
    available: [],
  }

  let display = {
    users: true,
    groups: true,
  }

  const loading = {}

  async function fetchAll() {
    try {
      loading.members = true
      await Promise.all([fetchGroups(), fetchUsers()]).then(() => {
        mapMembers()
      })
    } finally {
      loading.members = false
    }
  }

  async function fetchUsers() {
    const roleUsersList = await RolesRepository.getRoleUsers(id)
    roleUsers = roleUsersList.list

    const usersList = await UsersRepository.getUsers()
    allUsers = usersList.list
  }

  async function fetchGroups() {
    loading.groups = true

    if (!$isAuthorized("group_read")) {
      roleGroups = []
      return null
    }
    const groupsList = await GroupsRepository.getGroups()
    allGroups = groupsList.list

    const roleGroupsList = await RolesRepository.getRoleGroups(id)
    roleGroups = roleGroupsList.list

    roleGroups = roleGroups.map((g) => {
      const currGroup = allGroups.find((ag) => ag.id === g)
      return currGroup
    })
  }

  async function mapMembers() {
    assignmentGridObj = {
      available: [],
      current: [],
    }

    const mapUser = (u) => ({
      type: "user",
      id: u.id.toString(),
      label: `${u.firstName} ${u.lastName}`,
      info: u.email,
    })

    const mappedRoleUsers = roleUsers.map(mapUser)
    assignmentGridObj.current.push(...mappedRoleUsers)

    const mappedAllUsers = allUsers.filter((u) => !roleUsers.some((ru) => ru.id === u.id)).map(mapUser)

    assignmentGridObj.available.push(...mappedAllUsers)

    const mapGroup = (g) => {
      return {
        type: "group",
        id: g.id,
        label: g.name,
        info: `${$_("common.members")} ${g.members}`,
      }
    }

    const mappedRoleGroups = roleGroups.map(mapGroup)
    assignmentGridObj.current.push(...mappedRoleGroups)
    const mappedAllGroups = allGroups.filter((g) => !roleGroups.some((rg) => rg.id === g.id)).map(mapGroup)

    assignmentGridObj.available.push(...mappedAllGroups)
    assignmentGridObj.available.sort((a, b) => a.label - b.label)
    assignmentGridObj.available = assignmentGridObj.available

    assignmentGridObj.current.sort((a, b) => a.label.localeCompare(b.label))
    assignmentGridObj.available.sort((a, b) => a.label.localeCompare(b.label))
  }

  async function handleSave() {
    loading.save = true

    const selected = assignmentGrid.getCurrent()

    const usersToAdd = selected.user ? selected.user.filter((uID) => !roleUsers.some((ru) => ru.id === uID)) : []

    const usersToRemove = selected.user ? roleUsers.filter((ru) => !selected.user.includes(ru.id)) : roleUsers

    const groupsToAdd = selected.group
      ? selected.group.filter((gID) => !roleGroups.some((rg) => rg.id.toString() === gID))
      : []

    const groupsToRemove = selected.group
      ? roleGroups.filter((rg) => !selected.group.includes(rg.id.toString()))
      : roleGroups

    if (
      usersToAdd.length === 0 &&
      usersToRemove.length === 0 &&
      groupsToAdd.length === 0 &&
      groupsToRemove.length === 0
    ) {
      callout.set({
        msg: $_("no.changes.callout"),
        status: "warning",
        timeout: true,
      })
      navigate("/roles")
      return
    }

    try {
      if (usersToAdd.length) await RolesRepository.addRoleUser(id, { users: usersToAdd })

      if (usersToRemove.length)
        await RolesRepository.deleteRoleUser(id, {
          users: usersToRemove.map((u) => u.id.toString()),
        })

      if (groupsToAdd.length) await RolesRepository.addRoleGroup(id, { groups: groupsToAdd })

      if (groupsToRemove.length)
        await RolesRepository.deleteRoleGroup(id, {
          groups: groupsToRemove.map((g) => g.id.toString()),
        })

      let calloutMsg = ""
      if (usersToAdd.length > 0) calloutMsg += `${usersToAdd.length} ${$_("assignment.callout.users.added")} `
      if (usersToRemove.length > 0) calloutMsg += `${usersToRemove.length} ${$_("assignment.callout.users.removed")} `
      if (groupsToAdd.length > 0) calloutMsg += `${groupsToAdd.length} ${$_("assignment.callout.groups.added")} `
      if (groupsToRemove.length > 0)
        calloutMsg += `${groupsToRemove.length} ${$_("assignment.callout.groups.removed")} `

      if (calloutMsg !== "") {
        callout.set({
          msg: calloutMsg,
          status: "success",
          timeout: true,
        })
      }

      navigate("/roles")
    } finally {
      fetchAll()
      loading.save = false
    }
  }

  onMount(() => {
    fetchAll()
  })
</script>

{#if role.missingPolicies}
  <MissingPoliciesCallout type="role" />
{/if}

{#if !loading.members}
  <AssignmentGrid
    data={assignmentGridObj}
    bind:this={assignmentGrid}
    disabled={!($isAuthorized("role_write") && $isAuthorized("user_write")) || role.missingPolicies}
  />

  {#if $isAuthorized("role_write") && $isAuthorized("user_write")}
    <div class="footer-buttons py-6">
      <Button on:click={() => assignmentGrid.reset()} outlined>{$_("common.reset")}</Button>
      <FlexItem pushEnd>
        <Button on:click={handleSave} class="mr-3" loading={loading.save}>{$_("buttons.save")}</Button>
        <Button on:click={() => navigate("/roles")} outlined>{$_("buttons.cancel")}</Button>
      </FlexItem>
    </div>
  {/if}
{:else}
  <Loader status="info" />
{/if}
